/**
 * Pagination component for API-driven data sets.
 *
 * This component renders a compact pagination control based on the total
 * number of pages and the currently active page. It provides navigation
 * actions (first, previous, next, last) and a dynamically calculated
 * set of visible page numbers with ellipsis truncation.
 *
 * It exists to encapsulate pagination logic and UI behavior while
 * delegating actual data fetching to the surrounding component via
 * state setters.
 *
 * @module
 *
 * @author Gregor Schalm gregor.schalm@wum-studios.de
 */

import Pagination from "react-bootstrap/Pagination";
import React from "react";

/**
 * Props for the PaginationApi component.
 *
 * @typedef {Object} PaginationApiProps
 * @property {number} length - Total number of available pages.
 * @property {number} active - Currently active page index (1-based).
 * @property {React.Dispatch<React.SetStateAction<number>>} setActive - State setter for updating the active page.
 * @property {React.Dispatch<React.SetStateAction<number>>} setFetchData - State setter used to trigger data refetching.
 */
interface PaginationApiProps {
    length: number;
    active: number;
    setActive: React.Dispatch<React.SetStateAction<number>>;
    setFetchData: React.Dispatch<React.SetStateAction<number>>;
}

/**
 * Renders a pagination control with dynamic page visibility.
 *
 * The component prevents invalid page transitions and triggers
 * a data reload via setFetchData when the active page changes.
 *
 * @param {PaginationApiProps} props - Pagination configuration and state handlers.
 * @returns {*} Rendered pagination component or null if only one page exists.
 *
 * @example
 * <PaginationApi
 *     length={10}
 *     active={1}
 *     setActive={setActive}
 *     setFetchData={setFetchData}
 * />
 */
const PaginationApi = ({
                           length,
                           active,
                           setActive,
                           setFetchData,
                       }: PaginationApiProps) => {
    if (length <= 1) {
        return null;
    }

    /**
     * Handles page changes and triggers data reload.
     *
     * Prevents navigation to invalid or already active pages.
     *
     * @param {number} page - Target page number.
     * @returns {void} Does not return a value.
     *
     * @example
     * changePage(3);
     */
    const changePage = (page: number): void => {
        if (page < 1 || page > length || page === active) {
            return;
        }

        setActive(page);
        setFetchData(Date.now());
    };

    const visiblePages = getVisiblePages(active, length, 2);

    return (
        <Pagination size="sm" className="float-end">
            <Pagination.First
                disabled={active === 1}
                onClick={() => changePage(1)}
            />
            <Pagination.Prev
                disabled={active === 1}
                onClick={() => changePage(active - 1)}
            />

            {visiblePages.map((item, index) => {
                if (item === "...") {
                    return <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />;
                }

                return (
                    <Pagination.Item
                        key={item}
                        active={item === active}
                        onClick={() => changePage(item)}
                    >
                        {item}
                    </Pagination.Item>
                );
            })}

            <Pagination.Next
                disabled={active === length}
                onClick={() => changePage(active + 1)}
            />
            <Pagination.Last
                disabled={active === length}
                onClick={() => changePage(length)}
            />
        </Pagination>
    );
};

/**
 * Calculates the visible page range including ellipsis placeholders.
 *
 * This function ensures that only a subset of pages is displayed
 * when the total number of pages is large. It always includes the
 * first and last page and dynamically shows pages around the current
 * page based on the sibling count.
 *
 * @param {number} currentPage - Currently active page.
 * @param {number} totalPages - Total number of pages.
 * @param {number} [siblingCount=2] - Number of adjacent pages to show on each side of the current page.
 * @returns {Array<number | "...">} Array of page numbers and ellipsis markers.
 *
 * @example
 * getVisiblePages(5, 20, 2);
 */
const getVisiblePages = (
    currentPage: number,
    totalPages: number,
    siblingCount: number = 2
): Array<number | "..."> => {
    if (totalPages <= 7) {
        return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages: Array<number | "..."> = [];

    const leftBoundary = Math.max(currentPage - siblingCount, 2);
    const rightBoundary = Math.min(currentPage + siblingCount, totalPages - 1);

    pages.push(1);

    if (leftBoundary > 2) {
        pages.push("...");
    }

    for (let page = leftBoundary; page <= rightBoundary; page++) {
        pages.push(page);
    }

    if (rightBoundary < totalPages - 1) {
        pages.push("...");
    }

    pages.push(totalPages);

    return pages;
};

export default PaginationApi;